import { useContext } from 'react';
import { FormikContext } from 'formik';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { FormTrackingContext } from '../Form';
import { useTranslation } from '../../hooks/i18n';
import { useUserLocation } from '../../hooks/useUserLocation';
import { logFieldChange } from '../../utils/tracking';
import { CommonProps } from './types';
import Typography from '../Typography';
import InfoButton from '../InfoButton';
import { COLORS } from '../../themev2';

export type TDateField = Dayjs | null;

type DateFieldProps = CommonProps & {
  label: string;
  hideLabel?: boolean;
  value?: TDateField;
  onChange?: (value: TDateField) => void;
  disableFuture?: boolean;
  minDate?: TDateField;
  maxDate?: TDateField;
  onlyMonthYear?: boolean;
  onlyYear?: boolean;
  readOnly?: boolean;
};

export default function DateField({
  fullWidth = true,
  name,
  label,
  hideLabel = false,
  value,
  onChange,
  error,
  eventLogger = logFieldChange,
  disableFuture = false,
  minDate,
  maxDate,
  moreInfoTooltip = null,
  optional = false,
  disabled = false,
  onlyMonthYear = false,
  onlyYear = false,
  readOnly = false,
}: DateFieldProps) {
  const { t } = useTranslation();
  const { language } = useUserLocation();
  const formik = useContext(FormikContext);
  const { trackingEvent } = useContext(FormTrackingContext);
  const _error = error || (formik?.touched[name] && formik?.errors[name]);

  const getFormat = () => {
    if (onlyMonthYear) return 'MM/YYYY';
    if (onlyYear) return 'YYYY';
    return language === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  };

  return (
    <FormControl error={!!_error} fullWidth={fullWidth} sx={hideLabel ? { mt: 0 } : undefined}>
      {!hideLabel && (
        <FormLabel
          sx={{
            padding: '8px 0',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Typography>{label}</Typography>
            {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
          </div>
          {optional && <Typography sx={{ color: COLORS.gray[1000] }}>{t('optional')}</Typography>}
        </FormLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          views={onlyMonthYear ? ['year', 'month'] : onlyYear ? ['year'] : undefined}
          value={value || formik?.values[name]}
          onChange={(newValue) => {
            formik && formik.setFieldValue(name, newValue);
            onChange && onChange(newValue);
            trackingEvent && eventLogger(trackingEvent, name, newValue.$d.toString());
          }}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          readOnly={readOnly}
          format={getFormat()}
          sx={{
            '& .MuiInputBase-root': {
              background: disabled && !readOnly ? COLORS.gray[300] : COLORS.brand.white,
            },
            '& .MuiInputBase-input': {
              padding: '12px',
              background: disabled && !readOnly ? COLORS.gray[300] : COLORS.brand.white,
            },
            '& .MuiInputBase-input::placeholder': {
              color: COLORS.gray[1000],
              opacity: 1,
            },
            width: '100%',
            '& fieldset': {
              borderColor: COLORS.gray[900],
              borderRadius: '8px',
            },
            '& svg': {
              color: '#3B4567',
            },
          }}
          slotProps={{
            day: {
              sx: {
                '&.MuiPickersDay-root.Mui-selected': {
                  backgroundColor: COLORS.secondary.indigo,
                  color: COLORS.brand.white,
                },
              },
            },
          }}
        />
      </LocalizationProvider>
      {!!_error && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {_error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
